import React from 'react';
import { useIntl } from 'react-intl';

import { BandStats, Chart, MostPlayed, Years } from 'interfaces';

import ConcertYearsList from 'components/ConcertYearsList';

import ArrowLink from 'components/ui/ArrowLink';
import BarChart from 'components/ui/BarChart';
import Title from 'components/ui/Title';
import SidebarBox from 'components/ui/SidebarBox';

import {
  Wrapper,
  ChartItem,
  ChartItemTitle
} from './StatisticSideBar.styles';

export interface StatisticSideBarProps {
  context: 'song' | 'allSongs' | 'album' | 'tour' | 'concert' | 'year' ;
  charts?: Chart[];
  sells?: number;
  songPlayed?: number;
  albumPlayed?: number;
  allShows?: number;
  mostPlayedSongs?: MostPlayed[];
  mostPlayedAlbums?: MostPlayed[];
  bandStats?: BandStats;
  years?: Years;
}

const StatisticSideBar = ({
  context,
  charts,
  sells,
  songPlayed = null,
  albumPlayed = null,
  allShows = null,
  mostPlayedSongs = null,
  mostPlayedAlbums = null,
  bandStats = null,
  years = null
}: StatisticSideBarProps) => {
  const intl = useIntl();

  return (
    <Wrapper>
      {
        context === 'allSongs' && bandStats ? (
          <SidebarBox>
            <Title element="h1" size="big" color="secondary">
              {intl.formatMessage({ id: `statisticSidebar.inNumbers` })}
            </Title>
            <table>
              <tbody>
              <tr>
                <td>{intl.formatMessage({ id: 'statisticSidebar.numberOf.songs' })}</td>
                <td><strong>{bandStats.songs}</strong></td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'statisticSidebar.numberOf.albums' })}</td>
                <td><strong>{bandStats.albums}</strong></td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'statisticSidebar.numberOf.internationalAlbums' })}</td>
                <td><strong>{bandStats.internationalAlbums}</strong></td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'statisticSidebar.numberOf.ep' })}</td>
                <td><strong>{bandStats.ep}</strong></td>
              </tr>
              </tbody>
            </table>
          </SidebarBox>
        ) : null
      }
      {
        (context === 'album' || context === 'song') && (charts?.length || sells) ? (
          <SidebarBox>
            <Title element="h1" size="big" color="secondary">
              {intl.formatMessage({ id: `statisticSidebar.album` })}
            </Title>
            {
              charts?.length ? (
                <React.Fragment>
                  <Title element="h2">{intl.formatMessage({ id: 'statisticSidebar.charts.title' })}</Title>
                  {
                    charts.map((chart, index) => (
                      <ChartItem key={index}>
                        <ChartItemTitle>{intl.formatMessage({ id: `statisticSidebar.charts.${ chart.name}` })}</ChartItemTitle>
                        <div>
                          <span>{intl.formatMessage({ id: 'statisticSidebar.charts.peak' }, { peak: chart.peak })}</span>
                          {
                            chart.count ? (
                              <span>{intl.formatMessage({ id: 'statisticSidebar.charts.count' }, { count: chart.count })}</span>
                            ) : null
                          }
                        </div>
                      </ChartItem>
                    ))
                  }
                </React.Fragment>
              ) : null
            }
            {
              sells ? (
                <React.Fragment>
                  <Title element="h2">{intl.formatMessage({ id: `statisticSidebar.album.sells.title` })}</Title>
                  <p>
                    {intl.formatNumber(sells, { style: 'currency', currency: 'JPN' })}
                  </p>
                </React.Fragment>
              ) : null
            }
          </SidebarBox>
        ) : null
      }
      <Title element="h1" size="big" color="secondary">
        {
          context === 'concert'
            ? intl.formatMessage({ id: 'statisticSidebar.onThisTour' })
            : null
        }
        {
          context === 'tour' || context === 'album' || context === 'song' || context === 'allSongs'
            ? intl.formatMessage({ id: 'statisticSidebar.onTour' })
            : null
        }
        {
          context === 'year'
            ? intl.formatMessage({ id: 'statisticSidebar.inYear' })
            : null
        }

      </Title>
      { /* @TODO: albums tours for songs and albums */ }
      {
        context !== 'song' && context !== 'concert' && mostPlayedSongs?.length ? (
          <SidebarBox>
            <Title element="h2">
              {intl.formatMessage({ id: `statisticSidebar.onTours.mostPlayedSongs` })}
            </Title>
            <BarChart list={mostPlayedSongs} />
          </SidebarBox>
        ) : null
      }
      {
        context === 'song' ? (
          <SidebarBox>
            <Title element="h1" size="normal" color="primary">
              {intl.formatMessage({ id: `statisticSidebar.inNumbers` })}
            </Title>
            <table>
              <tbody>
              <tr>
                <td>{intl.formatMessage({ id: 'statisticSidebar.onTours.songPlayed' })}</td>
                <td>
                  <div>
                    <strong>{songPlayed}</strong>
                    {intl.formatMessage({ id: `statisticSidebar.onTours.times` }, { count: songPlayed })}
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </SidebarBox>
        ) : null
      }
      {
        context === 'album' ? (
          <SidebarBox>
            <Title element="h1" size="normal" color="primary">
              {intl.formatMessage({ id: `statisticSidebar.inNumbers` })}
            </Title>
            <table>
              <tbody>
              <tr>
                <td>{intl.formatMessage({ id: 'statisticSidebar.onTours.albumPlayed' })}</td>
                <td>
                  <div>
                    <strong>{albumPlayed}</strong>
                    {intl.formatMessage({ id: `statisticSidebar.onTours.times` }, { count: albumPlayed })}
                  </div>
                  </td>
              </tr>
              </tbody>
            </table>
          </SidebarBox>
        ) : null
      }
      {
        context !== 'album' && context !== 'song' && mostPlayedAlbums?.length ? (
          <SidebarBox>
            <Title element="h2">
              {intl.formatMessage({ id: `statisticSidebar.onTours.mostPlayedAlbums` })}
            </Title>
            <BarChart list={mostPlayedAlbums} color="secondary" />
          </SidebarBox>
        ) : null
      }
      {
        context === 'tour' || context === 'concert' || context === 'year' ? (
          <React.Fragment>
            <SidebarBox>
              <Title element="h1" size="big" color="secondary">
                {intl.formatMessage({ id: `statisticSidebar.inNumbers` })}
              </Title>
              <table>
                <tbody>
                <tr>
                  <td>
                    {
                    context === 'concert'
                        ? intl.formatMessage({ id: 'statisticSidebar.onTours.allShows' })
                        : intl.formatMessage({ id: 'statisticSidebar.onTours.inTotal' })
                    }
                  </td>
                  <td>
                    <div>
                      <strong>{allShows}</strong>
                      {intl.formatMessage({ id: `statisticSidebar.onTours.shows` }, { count: allShows })}
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </SidebarBox>
          </React.Fragment>
        ) : null
      }
      {
        (context === 'tour' || context === 'concert' || context === 'year') && years ? (
          <SidebarBox>
            <Title element="h1" size="big" color="secondary">
              {intl.formatMessage({ id: `statisticSidebar.onTours.years` })}
            </Title>
            <ConcertYearsList list={years} />
            <ArrowLink to="/tours-and-shows/shows">
              {intl.formatMessage({ id: 'statisticSidebar.onTours.years.linkAll' })}
            </ArrowLink>
          </SidebarBox>
        ) : null
      }
    </Wrapper>
  );
};

export default StatisticSideBar;
